// fallow-ignore-file -- pre-existing complexity, verbatim move from CheckMaty (TES-1605 @plasser/ui extraction); refactor tracked separately
import * as React from "react";
import { ScrollArea, ScrollBar } from "./scroll-area";
import { TabsList } from "./tabs";
import { cn } from "../lib/utils";

interface ScrollableTabsListProps {
  children: React.ReactNode;
  className?: string;
  tabsListClassName?: string;
  /** The currently active tab value - used for auto-scrolling to active tab */
  activeTab?: string;
}

/**
 * A reusable scrollable tabs list component for mobile-responsive horizontal scrolling.
 * Wraps TabsList in a ScrollArea with visual styling, fade indicators, auto-scroll to active tab,
 * and enhanced touch swipe support with momentum scrolling.
 *
 * @example
 * <ScrollableTabsList activeTab={activeTab}>
 *   <TabsTrigger value="all">All (10)</TabsTrigger>
 *   <TabsTrigger value="open">Open (5)</TabsTrigger>
 * </ScrollableTabsList>
 */
export function ScrollableTabsList({
  children,
  className,
  tabsListClassName,
  activeTab,
}: ScrollableTabsListProps) {
  const [showLeftFade, setShowLeftFade] = React.useState(false);
  const [showRightFade, setShowRightFade] = React.useState(false);
  const [isTouching, setIsTouching] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const tabsListRef = React.useRef<HTMLDivElement>(null);

  // Touch tracking for momentum scrolling
  const touchRef = React.useRef({
    startX: 0,
    startScrollLeft: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
  });

  const checkScroll = React.useCallback(() => {
    const scrollElement = scrollRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (scrollElement) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollElement;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, []);

  // Touch event handlers for enhanced swipe with momentum
  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    const scrollElement = scrollRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    if (!scrollElement) return;

    setIsTouching(true);
    touchRef.current = {
      startX: e.touches[0].clientX,
      startScrollLeft: scrollElement.scrollLeft,
      lastX: e.touches[0].clientX,
      lastTime: Date.now(),
      velocity: 0,
    };
  }, []);

  const handleTouchMove = React.useCallback(
    (e: React.TouchEvent) => {
      if (!isTouching) return;

      const scrollElement = scrollRef.current?.querySelector(
        "[data-radix-scroll-area-viewport]",
      ) as HTMLElement;
      if (!scrollElement) return;

      const currentX = e.touches[0].clientX;
      const currentTime = Date.now();
      const deltaX = touchRef.current.lastX - currentX;
      const deltaTime = currentTime - touchRef.current.lastTime;

      // Calculate velocity for momentum
      if (deltaTime > 0) {
        touchRef.current.velocity = deltaX / deltaTime;
      }

      touchRef.current.lastX = currentX;
      touchRef.current.lastTime = currentTime;

      // Apply scroll
      const diff = touchRef.current.startX - currentX;
      scrollElement.scrollLeft = touchRef.current.startScrollLeft + diff;
    },
    [isTouching],
  );

  const handleTouchEnd = React.useCallback(() => {
    if (!isTouching) return;

    setIsTouching(false);

    const scrollElement = scrollRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    if (!scrollElement) return;

    // Apply momentum scrolling
    const velocity = touchRef.current.velocity;
    if (Math.abs(velocity) > 0.1) {
      const momentum = velocity * 150; // Momentum factor
      const targetScroll = scrollElement.scrollLeft + momentum;

      scrollElement.scrollTo({
        left: Math.max(
          0,
          Math.min(targetScroll, scrollElement.scrollWidth - scrollElement.clientWidth),
        ),
        behavior: "smooth",
      });
    }
  }, [isTouching]);

  // Scroll element into view helper
  const scrollElementIntoView = React.useCallback((element: HTMLElement) => {
    const scrollElement = scrollRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement;
    if (!scrollElement) return;

    const containerRect = scrollElement.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const elementLeftRelative = elementRect.left - containerRect.left + scrollElement.scrollLeft;
    const elementRightRelative = elementLeftRelative + elementRect.width;
    const visibleLeft = scrollElement.scrollLeft;
    const visibleRight = scrollElement.scrollLeft + containerRect.width;

    if (elementLeftRelative < visibleLeft || elementRightRelative > visibleRight) {
      const targetScroll = elementLeftRelative - containerRect.width / 2 + elementRect.width / 2;
      scrollElement.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: "smooth",
      });
    }
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!tabsListRef.current) return;

      const triggers = Array.from(
        tabsListRef.current.querySelectorAll('[role="tab"]:not([disabled])'),
      ) as HTMLElement[];

      if (triggers.length === 0) return;

      const currentIndex = triggers.findIndex((trigger) => trigger === document.activeElement);

      let nextIndex: number;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          nextIndex = currentIndex <= 0 ? triggers.length - 1 : currentIndex - 1;
          break;
        case "ArrowRight":
          e.preventDefault();
          nextIndex = currentIndex >= triggers.length - 1 ? 0 : currentIndex + 1;
          break;
        case "Home":
          e.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          e.preventDefault();
          nextIndex = triggers.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex >= 0 && triggers[nextIndex]) {
        triggers[nextIndex].focus();
        scrollElementIntoView(triggers[nextIndex]);
      }
    },
    [scrollElementIntoView],
  );

  // Scroll to active tab when it changes
  React.useEffect(() => {
    if (!activeTab || !tabsListRef.current) return;

    const activeTabElement = tabsListRef.current.querySelector(
      `[data-state="active"], [value="${activeTab}"][data-state="active"]`,
    ) as HTMLElement;

    if (activeTabElement) {
      scrollElementIntoView(activeTabElement);
    }
  }, [activeTab, scrollElementIntoView]);

  React.useEffect(() => {
    const scrollElement = scrollRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (scrollElement) {
      // Initial check
      checkScroll();

      // Add scroll listener
      scrollElement.addEventListener("scroll", checkScroll);

      // Add resize observer
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollElement);

      return () => {
        scrollElement.removeEventListener("scroll", checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [checkScroll]);

  return (
    <div
      className="relative touch-pan-x"
      ref={scrollRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onKeyDown={handleKeyDown}
    >
      <ScrollArea
        className={cn(
          "w-full rounded-lg bg-muted/50 p-1 transition-transform duration-100",
          isTouching && "scale-[0.995]",
          className,
        )}
      >
        <TabsList
          ref={tabsListRef}
          className={cn(
            "inline-flex h-auto min-h-10 w-max gap-1 bg-transparent p-1",
            tabsListClassName,
          )}
        >
          {children}
        </TabsList>
        <ScrollBar orientation="horizontal" className="invisible" />
      </ScrollArea>

      {/* Left fade indicator */}
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-8 rounded-l-lg bg-gradient-to-r from-muted/80 to-transparent transition-opacity duration-200",
          showLeftFade ? "opacity-100" : "opacity-0",
        )}
        aria-hidden="true"
      />

      {/* Right fade indicator */}
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-lg bg-gradient-to-l from-muted/80 to-transparent transition-opacity duration-200",
          showRightFade ? "opacity-100" : "opacity-0",
        )}
        aria-hidden="true"
      />
    </div>
  );
}
